import type { Artifact } from '@/models/artifact'

/**
 * Meta written during mid-flight interpretation. Durable sync keeps capture-time
 * meta on pending artifacts and full meta once interpretation is ready.
 */
const TRANSIENT_META_KEYS = new Set([
  'ocrPhase',
  'ocrText',
  'ocrEngine',
  'ocrConfidence',
  'ocrAt',
  'visionModel',
  'transcriptPhase',
  'draftTranscript',
  'draftModel',
  'draftTranscribedAt',
  'hqModel',
  'hqTranscribedAt',
  'modality',
])

const isInterpreted = (artifact: Artifact): boolean =>
  artifact.status === 'ready' && Boolean(artifact.interpretedText?.trim())

const durableMeta = (
  meta: Record<string, unknown>,
  interpreted: boolean,
): Record<string, unknown> => {
  if (interpreted) return { ...meta }
  const next: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(meta)) {
    if (TRANSIENT_META_KEYS.has(key)) continue
    next[key] = value
  }
  return next
}

/**
 * Canonical artifact value for the sync journal.
 * Processing status (interpreting / failed), errors, and mid-flight meta stay local.
 */
export const toDurableArtifact = (artifact: Artifact): Artifact => {
  const interpreted = isInterpreted(artifact)
  const base: Artifact = {
    id: artifact.id,
    kind: artifact.kind,
    payload: artifact.payload,
    status: interpreted ? 'ready' : 'pending',
    parentId: artifact.parentId,
    parentKind: artifact.parentKind,
    meta: durableMeta(artifact.meta, interpreted),
    createdAt: artifact.createdAt,
    // Pending tips share createdAt so processing-only updatedAt flips don't fork.
    updatedAt: interpreted ? artifact.updatedAt : artifact.createdAt,
  }
  if (interpreted) {
    return { ...base, interpretedText: artifact.interpretedText!.trim() }
  }
  return base
}

export const durableArtifactKey = (artifact: Artifact): string =>
  JSON.stringify(toDurableArtifact(artifact))

export const artifactsDurableEqual = (a: Artifact, b: Artifact): boolean =>
  durableArtifactKey(a) === durableArtifactKey(b)

/** Prefer a completed interpretation; otherwise newest tip. */
export const preferArtifactTip = <T extends { value?: Artifact; createdAt: string }>(
  tips: readonly T[],
): T | null => {
  if (tips.length === 0) return null
  const ready = tips.filter((tip) => tip.value && isInterpreted(tip.value))
  const pool = ready.length > 0 ? ready : tips
  return [...pool].sort((a, b) => a.createdAt.localeCompare(b.createdAt)).at(-1) ?? null
}

/**
 * When materializing a remote pending tip, keep local interpreting/failed UX
 * if the durable payload matches.
 */
export const mergeProjectedArtifact = (
  local: Artifact | null | undefined,
  remote: Artifact,
): Artifact => {
  if (!local) return remote
  if (isInterpreted(remote)) return remote
  // A lagging pending projection must never erase a completed local result.
  // The ready artifact will journal its durable child revision independently.
  if (isInterpreted(local)) return local
  if (
    artifactsDurableEqual(local, remote)
  ) {
    return local
  }
  return remote
}

export const isBlobMissingError = (error: unknown): boolean => {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : ''
  const lower = message.toLowerCase()
  return (
    lower.includes('blob missing from local storage') ||
    lower.includes('blob not local yet') ||
    lower.includes('artifact blob not local')
  )
}
